import { useDropzone } from 'react-dropzone';
import { Alert, Button, Card, CardContent, Container, Grid, Stack, TextField, Typography, Box, FormControlLabel, Switch, CircularProgress, IconButton } from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { getFirestore, doc, updateDoc, getDoc, addDoc, setDoc, collection, deleteField, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileUploader } from "react-drag-drop-files";
import DeleteIcon from '@mui/icons-material/Delete';

import firebaseApp from '../utils/firebaseConfig'; // make sure the path is correct
import { stripAccents } from '../utils/search';
import MemberRenewalCard from '../sections/@dashboard/user/MemberRenewalCard';
import MemberLayawaysCard from '../sections/@dashboard/layaways/MemberLayawaysCard';
import MemberReservationsCard from '../sections/@dashboard/user/MemberReservationsCard';

export default function NewUserPage() {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState(''); // fin de membresía (expiration)
    // Fecha en que se creó la cuenta: SOLO informativa, nunca se edita
    const [createdAt, setCreatedAt] = useState(null);
    const [licenseNumber, setLicenseNumber] = useState('');
    const [licenseExpiration, setLicenseExpiration] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [file, setFile] = useState(null);
    const [profilePic, setProfilePic] = useState('');
    const [memberId, setMemberId] = useState('');
    const [userStatus, setUserStatus] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    // Reset de PIN por admin: si se escribe un PIN de 4 dígitos, se aplica
    // en members.pin (app) y en user-pins (web) al guardar.
    const [newPin, setNewPin] = useState('');
    // ¿La cuenta ya tiene PIN? (solo informativo; el valor nunca se muestra)
    const [hasPin, setHasPin] = useState(false);
    const [removingPin, setRemovingPin] = useState(false);

    const navigate = useNavigate();
    const navLocation = useLocation();

    const userId = new URLSearchParams(navLocation.search).get('id');
    const fileTypes = ['JPG', 'PNG', 'GIF'];

    useEffect(() => {
        const fetchUser = async () => {
            if (!userId) return;

            try {
                const db = getFirestore(firebaseApp);
                const userDocRef = doc(db, 'members', userId);
                const userDocSnapshot = await getDoc(userDocRef);

                if (!userDocSnapshot.exists()) {
                    alert('Este usuario ya no existe en la base de datos (posiblemente fue borrado). Si guardas, se volverá a crear con la información que pongas.');
                }
                if (userDocSnapshot.exists()) {
                    const userData = userDocSnapshot.data();
                    const nameParts = (userData.fullName || '').split(' ');
                    setFirstName(nameParts[0] || '');
                    setLastName(nameParts.slice(1).join(' ') || '');
                    setEmail(userData.email || '');
                    setHasPin(Boolean(userData.pin));
                    setDateOfBirth(convertFirestoreTimestampToDate(userData.expiration) || '');
                    setCreatedAt(userData.createdAt || null);
                    setLicenseNumber(userData.licenseNumber || userData.gunLicense || '');
                    setLicenseExpiration(convertFirestoreTimestampToDate(userData.licenseExpiration) || '');
                    setPhoneNumber(userData.phone || '');
                    setMemberId(userData.memberId || '');
                    setUserStatus(userData.active || false);
                    setProfilePic(userData.photo?.[0]?.downloadURL || '');
                }
            } catch (error) {
                console.error('Error fetching user:', error);
            }
        };

        fetchUser();
    }, [userId]);

    const handleFirstNameChange = (event) => {
        setFirstName(event.target.value);
    };

    const handleLastNameChange = (event) => {
        setLastName(event.target.value);
    };

    const handleEmailChange = (event) => {
        setEmail(event.target.value);
    };

    const handleMemberIdChange = (event) => {
        setMemberId(event.target.value);
    };

    const handleDateOfBirthChange = (event) => {
        setDateOfBirth(event.target.value);
    };

    const handlePhoneNumberChange = (event) => {
        // Se guarda SOLO el número (sin rayas ni paréntesis) para que la
        // búsqueda por teléfono siempre lo encuentre
        setPhoneNumber(event.target.value.replace(/\D/g, ''));
    };

    const handleEventStatusChange = (event) => {
        setUserStatus(event.target.checked);
    };

    // Aviso en vivo cuando la fecha de expiración y el switch de Active no
    // hacen lógica (pedido de Afía 2026-08-24: el caso "fecha vigente pero no
    // lo puse en verde" no avisaba nada y se le quedaban sin activar).
    const statusMismatch = (() => {
        if (!dateOfBirth) return null;
        const d = new Date(`${dateOfBirth}T12:00:00`);
        if (Number.isNaN(d.getTime())) return null;
        const dateValid = d.getTime() >= Date.now();
        if (dateValid && !userStatus) return 'inactive_valid';
        if (!dateValid && userStatus) return 'active_expired';
        return null;
    })();

    const handleProfileChange = (file) => {
        setFile(file);
    };

    function generateRandomCode() {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = '';
        for (let i = 0; i < 6; i += 1) {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        return result;
    }

    const saveImageToFirestore = async () => {
        let profileImageUrl = '';
        try {
            const storage = getStorage();
            const storageRef = ref(storage);

            if (file) {
                const coverImageRef = ref(storageRef, file.name);
                await uploadBytes(coverImageRef, file);
                profileImageUrl = await getDownloadURL(coverImageRef);
            }

            return profileImageUrl;

        } catch (error) {
            alert('Error uploading file!')
        }

        return profileImageUrl;
    };

    // Quitar el PIN (2026-11-03): el staff ponía un PIN sin querer al crear la
    // cuenta y el cliente veía "PIN incorrecto" en el app. Sin PIN, el app le
    // muestra "Configura tu PIN" la próxima vez que entre. Se borra en
    // members.pin (app) y en user-pins (web).
    const removePin = async () => {
        if (!userId) return;
        if (!window.confirm('¿Quitar el PIN de esta cuenta?\n\nEl cliente creará uno nuevo la próxima vez que entre al app o a la web.')) return;
        setRemovingPin(true);
        try {
            const db = getFirestore(firebaseApp);
            const snap = await getDoc(doc(db, 'members', userId));
            const cur = snap.exists() ? snap.data() : {};
            await updateDoc(doc(db, 'members', userId), { pin: deleteField(), pinRemovedAt: new Date() });
            const ids = [...new Set([cur.email, email.trim().toLowerCase(), cur.phone, phoneNumber].map((v) => String(v || '').trim()).filter(Boolean))];
            await Promise.all(ids.map((i) => deleteDoc(doc(db, 'user-pins', i)).catch(() => {})));
            setHasPin(false);
            setNewPin('');
            alert('PIN eliminado. El cliente creará su PIN la próxima vez que entre al app.');
        } catch (error) {
            console.error('Error removing PIN:', error);
            alert(`No se pudo quitar el PIN: ${error.message}`);
        } finally {
            setRemovingPin(false);
        }
    };

    const saveUserToFirestore = async () => {
        setIsSaving(true);

        const db = getFirestore(firebaseApp);
        let localProfileImage = profilePic;

        if (file) {
            const profileImageUrl = await saveImageToFirestore();
            localProfileImage = profileImageUrl;
        }

        // Fechas: mediodía local para evitar corrimiento de día por zona horaria.
        // Un campo vacío o inválido se guarda como null (antes: new Date('') =
        // Invalid Date → updateDoc explotaba y NADA se guardaba, ni el Active).
        const parseDate = (str) => {
            if (!str) return null;
            const d = new Date(`${str}T12:00:00`);
            return Number.isNaN(d.getTime()) ? null : d;
        };

        // Si la fecha está vigente pero el switch quedó apagado, ofrecer
        // activarlo aquí mismo antes de guardar (lo más probable es que se
        // le olvidó al renovar la membresía)
        let effectiveStatus = userStatus;
        const expForCheck = parseDate(dateOfBirth);
        if (expForCheck && expForCheck.getTime() >= Date.now() && !userStatus) {
            if (window.confirm('⚠️ La fecha de expiración está VIGENTE pero el usuario NO está marcado como Active.\n\n¿Quieres marcarlo como Active antes de guardar?\n\nOK = guardar como Active · Cancel = guardar tal como está')) {
                effectiveStatus = true;
                setUserStatus(true);
            }
        }

        try {
            const data = {
                active: effectiveStatus,
                ...(newPin.length === 4 ? { pin: newPin } : {}),
                email: email.trim().toLowerCase(),
                expiration: parseDate(dateOfBirth),
                licenseNumber: licenseNumber.trim(),
                licenseExpiration: parseDate(licenseExpiration),
                // Los nombres se guardan SIN acentos para que la búsqueda
                // del staff siempre encuentre a la persona
                fullName: stripAccents(`${firstName} ${lastName}`.trim()),
                memberId,
                phone: phoneNumber,
                // Solo guardamos photo si hay imagen; así no borra la
                // existente ni deja un downloadURL vacío que se ve roto.
                ...(localProfileImage ? { photo: [{ downloadURL: localProfileImage }] } : {}),
            };

            if (userId) {
                // setDoc con merge: si el doc fue borrado (ej: el cliente borró
                // su cuenta desde el app), se RE-CREA en vez de fallar con
                // "No document to update".
                await setDoc(doc(db, 'members', userId), { ...data, id: userId }, { merge: true });
                // Verificar que quedó guardado de verdad
                const check = await getDoc(doc(db, 'members', userId));
                if (!check.exists() || check.data().active !== effectiveStatus) {
                    throw new Error('La verificación después de guardar no coincide.');
                }
            } else {
                await addDoc(collection(db, 'members'), { ...data, id: generateRandomCode(), createdAt: new Date() });
            }

            // PIN nuevo → aplicarlo también en user-pins (web) por email y teléfono
            if (newPin.length === 4) {
                const pinIds = [...new Set([data.email, (phoneNumber || '').trim()].filter(Boolean))];
                await Promise.all(pinIds.map((i) =>
                    setDoc(doc(db, 'user-pins', i), { pin: newPin, updatedAt: new Date() }, { merge: true })
                ));
            }

            setIsSaving(false);
            alert('Usuario guardado correctamente.');
            navigate('/dashboard/user');
        } catch (error) {
            console.error('Error saving user document:', error);
            setIsSaving(false);
            alert(`Error guardando el usuario: ${error.message}. Verifica los campos e intenta de nuevo.`);
        }
    };

    // Fecha legible de creación de la cuenta (Timestamp, Date o string)
    function formatCreatedAt(value) {
        if (!value) return 'No disponible';
        let date = null;
        if (typeof value.seconds === 'number') date = new Date(value.seconds * 1000);
        else if (value instanceof Date) date = value;
        else if (typeof value === 'string') date = new Date(value);
        if (!date || Number.isNaN(date.getTime())) return 'No disponible';
        return date.toLocaleString('es-PR', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: 'numeric', minute: '2-digit',
        });
    }

    function convertFirestoreTimestampToDate(timestamp) {
        if (!timestamp || typeof timestamp.seconds !== 'number' || typeof timestamp.nanoseconds !== 'number') {
            return timestamp;
        }

        const milliseconds = timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000;
        const date = new Date(milliseconds);
        return date.toISOString().slice(0, 10);
    }

    return (
        <>
            <Helmet>
                <title> Users | BBTC </title>
            </Helmet>
            <Container>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                    <Typography variant="h4" gutterBottom>
                        User Information
                    </Typography>
                </Stack>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <CardContent sx={{ padding: 2 }}>
                                <Stack>
                                    <Typography variant="h6" sx={{ marginBottom: 2 }}>User Details</Typography>
                                    <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                                        <Grid item xs={6}>
                                            <TextField fullWidth label="First Name" value={firstName} onChange={handleFirstNameChange} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth label="Last Name" value={lastName} onChange={handleLastNameChange} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth label="Email" value={email} onChange={handleEmailChange} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth label="Phone Number" value={phoneNumber} onChange={handlePhoneNumberChange} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth label="Member ID" value={memberId} onChange={handleMemberIdChange} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label="Cuenta Creada"
                                                value={formatCreatedAt(createdAt)}
                                                InputProps={{ readOnly: true }}
                                                disabled
                                                helperText="Fecha en que se creó la cuenta. No se puede editar."
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField type="date" fullWidth label="Fin de Membresía (Expiración)" value={dateOfBirth} onChange={handleDateOfBirthChange} InputLabelProps={{
                                                shrink: true,
                                            }} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth label="Número de Licencia de Armas" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField type="date" fullWidth label="Expiración de Licencia de Armas" value={licenseExpiration} onChange={(e) => setLicenseExpiration(e.target.value)} InputLabelProps={{
                                                shrink: true,
                                            }} />
                                        </Grid>
                                        {/* PIN: solo al EDITAR. Al crear una cuenta no se pide PIN:
                                            el cliente lo crea en el app ("Configura tu PIN"). Antes el
                                            campo salía también al crear y el staff lo llenaba sin querer
                                            → el cliente veía "PIN incorrecto" (reporte 2026-11-03). */}
                                        {userId ? (
                                            <Grid item xs={12} md={6}>
                                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                                    <TextField
                                                        fullWidth
                                                        label={hasPin ? 'Cambiar PIN (4 dígitos)' : 'Asignar PIN (4 dígitos)'}
                                                        // Enmascarado (puntos) para que el staff no vea el PIN
                                                        // del cliente (pedido del cliente 2026-09-28).
                                                        type="password"
                                                        autoComplete="new-password"
                                                        value={newPin}
                                                        onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                                        inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                                                        helperText={hasPin
                                                            ? 'Esta cuenta YA tiene PIN. Déjalo vacío para no cambiarlo. Si el cliente no lo sabe, usa "Quitar PIN".'
                                                            : 'Esta cuenta NO tiene PIN: el cliente lo crea al entrar al app. Escribe uno solo si el cliente te lo pide.'}
                                                    />
                                                    {hasPin && (
                                                        <Button variant="outlined" color="warning" onClick={removePin} disabled={removingPin || isSaving} sx={{ whiteSpace: 'nowrap', mt: 1 }}>
                                                            {removingPin ? <CircularProgress size={18} /> : 'Quitar PIN'}
                                                        </Button>
                                                    )}
                                                </Stack>
                                            </Grid>
                                        ) : (
                                            <Grid item xs={12} md={6}>
                                                <Alert severity="info" sx={{ py: 0.5 }}>
                                                    No hace falta PIN: el cliente lo crea la primera vez que entra al app con su teléfono o email.
                                                </Alert>
                                            </Grid>
                                        )}
                                        <Grid item xs={6}>
                                            <FormControlLabel control={<Switch checked={userStatus} onChange={handleEventStatusChange} />} label="Active" />
                                        </Grid>
                                        {statusMismatch === 'inactive_valid' && (
                                            <Grid item xs={12}>
                                                <Alert
                                                    severity="warning"
                                                    action={
                                                        <Button color="inherit" size="small" onClick={() => setUserStatus(true)}>
                                                            Marcar Active
                                                        </Button>
                                                    }
                                                >
                                                    La fecha de expiración está vigente pero el usuario NO está marcado como Active. ¿Se te olvidó ponerlo en verde?
                                                </Alert>
                                            </Grid>
                                        )}
                                        {statusMismatch === 'active_expired' && (
                                            <Grid item xs={12}>
                                                <Alert severity="error">
                                                    El usuario está marcado como Active pero la fecha de expiración ya pasó. Actualiza la fecha o apaga el switch.
                                                </Alert>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" style={{ marginBottom: 30 }}>
                                    Profile Picture
                                </Typography>
                                <FileUploader handleChange={handleProfileChange} name="file" types={fileTypes} />
                                {(file && file.type.includes('image/')) || profilePic ? (
                                    <Box display="flex" flexDirection="column" alignItems="left" style={{ marginTop: 30 }}>
                                        <div style={{ position: 'relative', width: 100, height: 100 }}>
                                            <img src={file ? URL.createObjectURL(file) : profilePic} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Cover" />
                                            <IconButton
                                                onClick={() => {
                                                    setFile(null);
                                                    setProfilePic('');
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    background: 'white',
                                                    color: 'red',
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </div>
                                    </Box>
                                ) : null}
                            </CardContent>
                        </Card>
                    </Grid>
                    {userId && (
                        <Grid item xs={12} sm={6}>
                            <MemberRenewalCard memberId={userId} />
                        </Grid>
                    )}
                    {userId && (
                        <Grid item xs={12}>
                            <MemberReservationsCard member={{ id: userId, phone: phoneNumber, email }} />
                        </Grid>
                    )}
                    {userId && (
                        <Grid item xs={12}>
                            <MemberLayawaysCard member={{ id: userId, fullName: `${firstName} ${lastName}`.trim(), phone: phoneNumber, email, memberId }} />
                        </Grid>
                    )}
                </Grid>
                <Box sx={{ position: 'fixed', right: 16, bottom: 16 }}>
                    <Button variant="contained" color="primary" onClick={saveUserToFirestore}>
                        Save User
                    </Button>
                </Box>
            </Container>
            {isSaving && (
                <Box
                    position="fixed"
                    top="0"
                    left="0"
                    width="100%"
                    height="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor="rgba(255, 255, 255, 0.5)"
                >
                    <CircularProgress color="primary" />
                </Box>
            )}
        </>
    );
}
